import { OpenFin } from '@openfin/core'
import * as Notifications from '@openfin/notifications'
import { useCallback, useEffect } from 'react'
import {
  autoStartElapsed,
  isOpenFinRuntime,
  isOpenFinView,
  MAIN_FRAME_NAME,
  MAIN_WINDOW_NAME,
  NotificationTasks,
  openFinConfig
} from '../../containers/OpenFin/helpers'
import { closePopout, popoutSecurity } from '../../store/depthOfMarket/actions'
import { getPopoutSecurityIds } from '../../store/depthOfMarket/selectors'
import { confirmOrder, rejectOrder } from '../../store/lastLook/actions'
import { resetListTradingTransactionId } from '../../store/listTrading/actions'
import { getMarketClosed } from '../../store/market/selectors'
import { setTradeConfirmId } from '../../store/order/actions'
import { removeGrid } from '../../store/securities/actions'
import {
  StoreAction,
  useAppDispatch,
  useAppSelector,
  useAppStore
} from '../../store/types'
import { getIsAdmin } from '../../store/webSettings/selectors'
import { removeLastLookFromWindows } from '../../store/windows/actions'
import { storeHydration } from '../hydration'
import { buildHydration } from '../hydrationBuilders'
import { parseChildviewName } from '../snapshot'

export interface WindowAction {
  close(): void
  hide(): void
  minimize(): void
  restore(): void
  show(): void
}

type Interval = ReturnType<typeof setInterval>

interface OpenFinStartupParameters {
  openfinAutoStart: boolean
  openfinStartTime: string
}

// ------------------------WINDOW INFO------------------------ //
const getChildWindows = () => {
  return fin.Application.getCurrentSync().getChildWindows()
}

const getTargetWindow = async (name: string) => {
  const childWindows = await getChildWindows()
  return childWindows.find((win) => win.identity.name === name)
}

// undefined → no sibling open yet; caller opens its own window.
const resolveDockTarget = async (
  dockTarget: 'mainWindow' | 'firstSibling',
  baseName: string
): Promise<OpenFin.Identity | undefined> => {
  if (dockTarget === 'mainWindow') {
    return { uuid: fin.me.identity.uuid, name: MAIN_FRAME_NAME }
  }
  const childWindows = await getChildWindows()
  const pattern = new RegExp(`^${baseName}/\\d+$`)
  return childWindows.find((w) => pattern.test(w.identity.name))?.identity
}

const findMonitor = (monitorInfo: OpenFin.MonitorInfo, pos: SavedPosition) =>
  [monitorInfo.primaryMonitor, ...monitorInfo.nonPrimaryMonitors].find((m) => {
    const { left, top, right, bottom } = m.available.dipRect
    return (
      pos.left >= left && pos.left < right && pos.top >= top && pos.top < bottom
    )
  })

const isOnScreen = (monitorInfo: OpenFin.MonitorInfo, pos: SavedPosition) =>
  !!findMonitor(monitorInfo, pos)

// room to the right of pos, since resizeTo anchors top-left and grows rightward
export const availableWidthAt = (
  monitorInfo: OpenFin.MonitorInfo,
  pos: SavedPosition
) => {
  const { dipRect } = (
    findMonitor(monitorInfo, pos) ?? monitorInfo.primaryMonitor
  ).available
  return dipRect.right - pos.left
}

const getWindowPosition = async (baseName: string) => {
  try {
    const monitorInfo = await fin.System.getMonitorInfo()
    const saved = readWindowPositions()[baseName]
    if (saved && isOnScreen(monitorInfo, saved)) {
      return { defaultLeft: saved.left, defaultTop: saved.top }
    }
  } catch (e) {
    console.warn(e)
  }
  return { defaultCentered: true }
}

// extra height added below the view for the platform window's title/tab bar
const HEADER_HEIGHT_WITH_TABS = 60
const FRAME_HEADER_HEIGHT = 30

const resizeWindowToContent = async (
  targetWindow: OpenFin.Window,
  baseName: string,
  incomingWidth: number,
  incomingHeight?: number
) => {
  await targetWindow.updateOptions({ maxWidth: incomingWidth })
  const bounds = await targetWindow.getBounds()
  const options = await targetWindow.getOptions()
  const config = openFinConfig[baseName]
  const headerHeight =
    config?.hasHeaders === false ? FRAME_HEADER_HEIGHT : HEADER_HEIGHT_WITH_TABS
  const height =
    incomingHeight !== undefined ? incomingHeight + headerHeight : bounds.height
  targetWindow
    .resizeTo(options.maxWidth, height, 'top-left')
    .catch(console.warn)
}

export const changeDimensions = async (
  currentWindow: string,
  incomingWidth: number,
  incomingHeight?: number
) => {
  const targetWindow = await getTargetWindow(currentWindow)
  if (targetWindow) {
    await resizeWindowToContent(
      targetWindow,
      currentWindow.split('/')[0],
      incomingWidth,
      incomingHeight
    )
  }
}

// Resizes the view's current host window directly. changeDimensions can't: a
// torn-out window gets an OpenFin-generated name, so its name lookup misses.
export const resizeHostWindow = async (
  baseName: string,
  incomingWidth: number,
  incomingHeight?: number
) => {
  const targetWindow = await fin.View.getCurrentSync().getCurrentWindow()
  await resizeWindowToContent(
    targetWindow,
    baseName,
    incomingWidth,
    incomingHeight
  )
}

// .gridContainer min-width: 50rem, below which the pane overflows
const MIN_WINDOW_WIDTH = 500

/* Unlike resizeWindowToContent this never pins maxWidth, and clears a cap left
   by that path or by a restored snapshot (-1 is OpenFin's "no limit"). */
export const fitHostWindowWidth = async (
  desiredWidth: number,
  stillWanted: () => boolean = () => true
) => {
  if (!isOpenFinView() || desiredWidth <= 0 || !stillWanted()) return
  const targetWindow = await fin.View.getCurrentSync().getCurrentWindow()
  const [bounds, options, monitorInfo] = await Promise.all([
    targetWindow.getBounds(),
    targetWindow.getOptions(),
    fin.System.getMonitorInfo().catch(console.warn)
  ])
  // ahead of the no-op check: a stale cap truncates every later resize
  if (options.maxWidth !== -1) {
    await targetWindow.updateOptions({ maxWidth: -1 }).catch(console.warn)
  }
  const available = monitorInfo
    ? availableWidthAt(monitorInfo, bounds)
    : Number.POSITIVE_INFINITY
  // the floor wins: never squeeze a window near the screen edge to nothing
  const width = Math.max(Math.min(desiredWidth, available), MIN_WINDOW_WIDTH)
  // the tab may have been hidden across those awaits
  if (!stillWanted() || width === bounds.width) return
  await targetWindow
    .resizeTo(width, bounds.height, 'top-left')
    .catch(console.warn)
}

// ------------------------LOCAL STORAGE------------------------ //

const SNAPSHOT_KEY = 'openfin-snapshot'
const SNAPSHOT_DEBOUNCE_MS = 500

/* Last window position per base name; infinite-instance types (Depth,
   LastLook, Aggressor, RFQs, ...) share one entry. Only seeds windows the
   snapshot doesn't restore — snapshot bounds always win. */
const WINDOW_POSITIONS_KEY = 'openfin-window-positions'

interface SavedPosition {
  left: number
  top: number
}

const readWindowPositions = (): Record<string, SavedPosition> => {
  try {
    return JSON.parse(
      window.localStorage.getItem(WINDOW_POSITIONS_KEY) ?? '{}'
    ) as Record<string, SavedPosition>
  } catch {
    return {}
  }
}

/* unknown base names (the main frame, OpenFin-generated names for torn-out
   tabs) stay out of the positions map */
const saveWindowPosition = (name: string, left: number, top: number) => {
  const baseName = name.split('/')[0]
  if (!openFinConfig[baseName]) return
  const positions = readWindowPositions()
  positions[baseName] = { left, top }
  window.localStorage.setItem(WINDOW_POSITIONS_KEY, JSON.stringify(positions))
}
let boundsChangedTimer: ReturnType<typeof setTimeout> | null = null
let lastBoundsChanged: { name: string; left: number; top: number } | null = null

export const handleBoundsChanged = (event: {
  name: string
  left: number
  top: number
}) => {
  lastBoundsChanged = event
  if (boundsChangedTimer) clearTimeout(boundsChangedTimer)
  boundsChangedTimer = setTimeout(() => {
    if (lastBoundsChanged) {
      saveWindowPosition(
        lastBoundsChanged.name,
        lastBoundsChanged.left,
        lastBoundsChanged.top
      )
    }
    boundsChangedTimer = null
  }, SNAPSHOT_DEBOUNCE_MS)
  saveSnapshotDebounced()
}

// Set during sign-out teardown so view destruction doesn't re-save the snapshot.
// Never reset — sign-out always reloads this module.
let isSigningOut = false
export const getIsSigningOut = () => isSigningOut

export const saveSnapshotNow = async () => {
  if (isSigningOut) return
  try {
    const snapshot = await fin.Platform.getCurrentSync().getSnapshot()
    window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot))
  } catch (err) {
    console.warn('Failed to save OpenFin snapshot', err)
  }
}

export const saveSnapshotDebounced = (() => {
  let timer: ReturnType<typeof setTimeout> | null = null
  return () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      void saveSnapshotNow()
    }, SNAPSHOT_DEBOUNCE_MS)
  }
})()

let applySnapshotPromise: Promise<void> | null = null
export const applySavedSnapshot = (): Promise<void> => {
  if (applySnapshotPromise) return applySnapshotPromise
  applySnapshotPromise = (async () => {
    if (fin.me.identity.name !== MAIN_WINDOW_NAME) return
    const stored = window.localStorage.getItem(SNAPSHOT_KEY)
    if (!stored) return
    try {
      await fin.Platform.getCurrentSync().applySnapshot(
        JSON.parse(stored) as OpenFin.Snapshot
      )
    } catch (err) {
      // null the singleton so a later caller can retry
      applySnapshotPromise = null
      console.warn('Failed to apply OpenFin snapshot', err)
    }
  })()
  return applySnapshotPromise
}

// isSigningOut keeps the saved snapshot intact so popouts restore next sign-in.
export const closeAllPopouts = async () => {
  isSigningOut = true
  const app = fin.Application.getCurrentSync()
  try {
    const windows = await app.getChildWindows()
    await Promise.all(
      windows
        .filter((w) => w.identity.name !== MAIN_FRAME_NAME)
        .map((w) => w.close().catch(console.warn))
    )
  } catch (err) {
    console.warn('Failed to close popout windows', err)
  }
  try {
    const views = await app.getViews()
    await Promise.all(
      views
        .filter((v) => v.identity.name !== MAIN_WINDOW_NAME)
        .map((v) => v.destroy().catch(console.warn))
    )
  } catch (err) {
    console.warn('Failed to destroy popout views', err)
  }
}

// Per-base-name store cleanup for a destroyed popout view; unknown names
// (e.g. TradeConfirm — handled by its own 'closed' listener) are ignored.
const detachedViewActions: Record<string, (id: number) => StoreAction> = {
  Depth: closePopout,
  Watchlists: removeGrid
}

export const cleanupDetachedView = (
  name: string | undefined,
  dispatch: ReturnType<typeof useAppDispatch>
) => {
  const parsed = parseChildviewName(name)
  if (!parsed) return
  const makeAction = detachedViewActions[parsed.baseName]
  if (makeAction) dispatch(makeAction(parsed.id))
}

// Per-base-name store cleanup when a popout window closes; `name` is the full
// window name (e.g. LastLook/<orderId>).
const closedWindowActions: Record<string, (name: string) => StoreAction> = {
  TradeConfirm: () => setTradeConfirmId(null),
  ListTrading: () => resetListTradingTransactionId(),
  LastLook: (name) =>
    removeLastLookFromWindows({
      orderId: name.substring(name.indexOf('/') + 1),
      aggressorOrderId: ''
    })
}

export const destroyStaleViews = async (
  baseName: string,
  expectedIds: Set<number>
) => {
  const pattern = new RegExp(`^childview_${baseName}\\/(\\d+)$`)
  const allViews = await fin.Application.getCurrentSync().getViews()
  await Promise.all(
    allViews
      .filter((v) => {
        const m = v.identity.name.match(pattern)
        return !!m && !expectedIds.has(Number(m[1]))
      })
      .map((v) => v.destroy().catch(console.warn))
  )
}

export const useOpenFin = () => {
  const isOpenFin = isOpenFinRuntime()
  const marketClosed = useAppSelector(getMarketClosed)
  const isAdmin = useAppSelector(getIsAdmin)
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const popoutIds = useAppSelector(getPopoutSecurityIds)

  // ------------------------WINDOW CONTROLS------------------------ //

  const finWindowAction = useCallback(
    async (
      windowName: string | null,
      action: keyof WindowAction,
      useBaseName?: boolean,
      isSignout?: boolean
    ) => {
      if (!isOpenFin) return
      try {
        const performAction = async (win: OpenFin.Window) => {
          if (isSignout) {
            // keep main window open
            if (action === 'hide' && win.identity.name === MAIN_FRAME_NAME) {
              return
            }
            await win.close()
          } else {
            await win.setAsForeground()
            await win[action]()
          }
        }

        if (!windowName && isSignout) {
          await closeAllPopouts()
          return
        }

        const childWindows = await getChildWindows()
        // no name -> all child windows; useBaseName -> all sharing the base; else exact match
        const targets = !windowName
          ? childWindows
          : childWindows.filter((win) =>
              useBaseName
                ? win.identity.name.includes(windowName)
                : win.identity.name === windowName
            )
        await Promise.all(targets.map((child) => performAction(child)))
      } catch (err) {
        console.error(`Failed to ${action} windows:`, err)
      }
    },
    [isOpenFin]
  )

  useEffect(() => {
    // on nightly log out, close child windows
    if (isOpenFin && !isAdmin && marketClosed) {
      void (async () => {
        await finWindowAction(null, 'hide', false, true)
      })()
    }
  }, [isAdmin, isOpenFin, finWindowAction, marketClosed])

  // ------------------------CREATE WINDOWS------------------------ //
  const openPlatformWindow = useCallback(
    async (name: string) => {
      if (!isOpenFin) return

      const viewName = `childview_${name}`
      const existingViews = await fin.Application.getCurrentSync().getViews()
      if (existingViews.some((v) => v.identity.name === viewName)) {
        return
      }
      const baseName = name.split('/')[0]
      const {
        isAdminWindow = false,
        windowHeight,
        windowWidth,
        includeInSnapshots = true,
        hasHeaders = true,
        title = baseName,
        showTaskbarIcon = true
      } = openFinConfig[baseName] || {}
      const dockTarget = openFinConfig[baseName]?.dockTarget

      /* Written before either creation path so the docked view and the new window
         both get it; the popout reads it synchronously in getStore. */
      try {
        const hydrate = buildHydration(name, store.getState())
        if (hydrate) storeHydration(viewName, hydrate)
      } catch (err) {
        console.warn('Failed to build hydration payload', err)
      }

      const platform = fin.Platform.getCurrentSync()
      const url = `${window.location.origin}/${
        isAdminWindow ? 'admin/' : ''
      }${name}`

      if (dockTarget) {
        const target = await resolveDockTarget(dockTarget, baseName)
        if (target) {
          // Dock as a tab; snapshot + store cleanup run from App.tsx's view-attached/detached listeners.
          await platform.createView({ name: viewName, url }, target)
          return
        }
        // firstSibling, none open yet → fall through and create the first window.
      }

      const windowPosition = await getWindowPosition(baseName)
      const newWindow = await platform.createWindow({
        name,
        defaultWidth: windowWidth,
        defaultHeight: windowHeight,
        ...windowPosition,
        frame: false,
        shadow: true,
        showTaskbarIcon,
        includeInSnapshots,
        waitForPageLoad: true,
        customData: { title },
        layout: {
          settings: {
            hasHeaders
          },
          content: [
            {
              type: 'stack',
              content: [
                {
                  type: 'component',
                  componentName: 'view',
                  componentState: {
                    name: viewName,
                    url
                  }
                }
              ]
            }
          ]
        }
      })
      await newWindow.setAsForeground()

      await newWindow.addListener('closed', () => {
        const makeAction = closedWindowActions[baseName]
        if (makeAction) dispatch(makeAction(name))
      })
    },
    [dispatch, isOpenFin, store]
  )

  const syncPlatformWindows = useCallback(
    async (baseName: string, ids: number[]) => {
      if (!isOpenFin) return
      await applySavedSnapshot()
      await destroyStaleViews(baseName, new Set(ids))
      /* firstSibling reads whether a sibling window is open, so opening in
         parallel makes every id read none and take the new-window path */
      const needsAnchor =
        openFinConfig[baseName]?.dockTarget === 'firstSibling' && ids.length > 1
      if (needsAnchor) {
        await openPlatformWindow(`${baseName}/${ids[0]}`)
      }
      await Promise.all(
        (needsAnchor ? ids.slice(1) : ids).map((id) =>
          openPlatformWindow(`${baseName}/${id}`)
        )
      )
    },
    [isOpenFin, openPlatformWindow]
  )

  // ------------------------AUTOLAUNCH------------------------ //

  // the caller owns the returned handle, so an unmount can clear the poll
  const autoLaunch = (
    startupParameters: OpenFinStartupParameters
  ): Interval | undefined => {
    if (!startupParameters.openfinAutoStart) return
    let lastCheck = new Date()
    return setInterval(() => {
      const now = new Date()
      const elapsed = autoStartElapsed(
        startupParameters.openfinStartTime,
        lastCheck,
        now
      )
      lastCheck = now
      if (elapsed) finWindowAction(null, 'restore')
    }, 1000)
  }

  // ------------------------NOTIFICATIONS------------------------ //

  const notificationAction = useCallback(
    (event: Notifications.NotificationActionEvent) => {
      const { result } = event
      switch (result.task) {
        case NotificationTasks.ViewDetails:
          const { id } = result
          dispatch(setTradeConfirmId(Number(id.replace('O', ''))))

          break
        case NotificationTasks.PopoutDepth:
          if (!popoutIds.includes(result.id)) {
            dispatch(popoutSecurity(result.id))
          } else {
            finWindowAction(`Depth/${result.id}`, 'restore').catch(console.warn)
          }
          break
        case NotificationTasks.AcceptOrder:
          // TODO: spotHedgePref comes back undefined
          // use default for now
          dispatch(confirmOrder(result.id, 'D'))
          break
        case NotificationTasks.RejectOrder:
          dispatch(rejectOrder(result.id))
          break
      }
    },
    [dispatch, popoutIds, finWindowAction]
  )

  const registerNotifications = useCallback(() => {
    Notifications.register()
      .catch((err) => console.error(err))
      .then(() =>
        Notifications.addEventListener(
          'notification-action',
          notificationAction
        )
      )
      .catch((err) => console.error(err))
  }, [notificationAction])

  const deregisterNotifications = useCallback(() => {
    if (!isOpenFin) return
    Notifications.removeEventListener('notification-action', notificationAction)
      .then(() => Promise.all([Notifications.hide(), Notifications.clearAll()]))
      .catch((err) => console.error(err))
  }, [isOpenFin, notificationAction])

  return {
    autoLaunch,
    finWindowAction,
    openPlatformWindow,
    syncPlatformWindows,
    notificationAction,
    registerNotifications,
    deregisterNotifications,
    isOpenFin
  }
}
